import { FileText } from 'lucide-react';
import { SHOW_OUT_OF_SCOPE } from '@/lib/out-of-scope';
import { cn } from '@/lib/utils';

/**
 * How the engagement reads. Ordered by precedence — the first one that applies
 * wins, which is what `TimesheetEngagementCell` needs (an ambiguous row is
 * ambiguous whatever else is true of it).
 *
 *   ask          — 2+ engagements cover the entry and none is chosen. The only
 *                  state that blocks a submit, so it is the only loud one.
 *   unset        — nothing is attributed, but engagements exist to attribute
 *                  TO. A prompt, not a statement of absence: it is the only
 *                  entry point for booking time out of scope.
 *   out-of-scope — attributed to an engagement that does not list the
 *                  activity, which is a deliberate recording the server allows.
 *   bound        — attributed, in scope. The ordinary case.
 *
 * There is deliberately no tone for "there is no engagement". An absence with
 * nothing to offer against it is rendered as nothing at all — see
 * `engagementDisplay`, and the `items.length === 0` gate in
 * `TimesheetEngagementCell`.
 */
export type EngagementTone = 'ask' | 'unset' | 'out-of-scope' | 'bound';

/**
 * Whether an engagement line should be rendered at all, and how it should read
 * when it is. THE one definition, shared by the week grid, the day view and the
 * approvals queue — so a rule like "never announce the absence of an
 * engagement" lands in a single place instead of three.
 *
 * `null` means render nothing, and it covers three things that all amount to
 * "there is no engagement to show":
 *
 *   - the entry is genuinely unattributed. A legitimate, submittable state, and
 *     a line saying so is noise on every row with no SOW behind it.
 *   - nothing is attributed yet on a row that could take one. Silence beats a
 *     placeholder the user did not ask for.
 *   - the field never arrived at all. Web and the API are separate services, so
 *     a deploy landing web first serves rows carrying no engagement keys;
 *     rendering nothing is honest, where a placeholder would be a claim about
 *     billing that nobody made.
 *
 * An attributed entry whose NAME did not resolve still renders: `engagement_id`
 * has no FK behind it and prod SQL is applied by hand, so an id can outlive its
 * `sows` row. The one thing such an entry is not is unattributed.
 *
 * `ask` and `unset` are deliberately not expressible here. Both are
 * interactive-only states about what could still be PICKED, which a read-only
 * surface has no business rendering; they stay with the control that can act
 * on them.
 */
export function engagementDisplay(input: {
  engagementId?: string | null;
  engagementName?: string | null;
  /** False when the engagement does not list the entry's activity. Omit on a
   *  surface that cannot know — the approvals query does not join the retainer
   *  lines, so it never claims in-scope or out. */
  coversActivity?: boolean;
}): { label: string; tone: EngagementTone } | null {
  if (input.engagementId == null) return null;
  return {
    // `||`, not `??`: an empty name is as unusable as a missing one.
    label: input.engagementName || 'Engagement unavailable',
    // Out of scope reads as an ordinary attribution until the feature is
    // released — see `SHOW_OUT_OF_SCOPE`. Gated HERE, the one definition every
    // surface shares, so the tint, the tooltip and the screen-reader marker
    // all go together.
    tone: SHOW_OUT_OF_SCOPE && input.coversActivity === false ? 'out-of-scope' : 'bound',
  };
}

interface Props {
  /** What to render. The caller owns the wording — the engagement's name where
   *  one is bound, "Select engagement" where the row is ambiguous. */
  label: string;
  tone: EngagementTone;
  className?: string;
}

/**
 * The engagement (SOW) line, wherever one is shown: under the customer in the
 * timesheet week grid and day view, and on each entry row in the approvals
 * queue.
 *
 * Presentational and deliberately dumb — it takes a label and a tone and knows
 * nothing about resolution, candidates, or picking. That is what lets the
 * interactive timesheet cell and the read-only approvals row share it instead
 * of one of them re-implementing the treatment and drifting.
 *
 * A document icon, not a bullet: a dot read as list punctuation hanging under
 * the customer name, while the icon says "this line is the engagement" at a
 * glance and gives a stable left edge across every state. Out of scope tints
 * icon and text amber rather than appending "· out of scope" — these labels
 * live in columns that truncate from the right, so a suffix is the first thing
 * to disappear, exactly when it matters. Colour cannot carry it alone, so the
 * state also rides in an `sr-only` marker.
 */
export function EngagementLabel({ label, tone, className }: Props) {
  return (
    <span
      className={cn(
        'flex min-w-0 items-center gap-1 text-xs',
        tone === 'ask' && 'font-medium text-destructive',
        tone === 'unset' && 'text-muted-foreground italic',
        tone === 'out-of-scope' && 'font-medium text-amber-700 dark:text-amber-500',
        tone === 'bound' && 'font-medium text-foreground/75',
        className,
      )}
    >
      <FileText aria-hidden className="size-3 shrink-0 opacity-70" />
      <span className="min-w-0 truncate">{label}</span>
      {tone === 'out-of-scope' ? <span className="sr-only">(out of scope)</span> : null}
    </span>
  );
}
